import { useEffect, useRef, useState } from 'react'

// Root-relative so they work from /press-releases too. On `/` they're
// fragment-only navigations — the browser scrolls without reloading.
const LINKS = [
  { href: '/#engine', label: 'How it works' },
  { href: '/#platform', label: 'Platform' },
  { href: '/#results', label: 'Results' },
  { href: '/#trust', label: 'Trust' },
]

/** Keep in step with the `max-width` on the nav's collapse rules in landing.css. */
const DESKTOP = '(min-width: 860px)'

export default function LandingNav() {
  const [open, setOpen] = useState(false)
  const headerRef = useRef<HTMLElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)

  // The toggle is display:none above the breakpoint, so a panel left open
  // while the viewport grows would strand the menu with no way to close it.
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP)
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setOpen(false)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    if (!open) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      toggleRef.current?.focus()
    }

    const onPointerDown = (e: PointerEvent) => {
      if (headerRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  return (
    <header className="lp-nav" data-menu-open={open} ref={headerRef}>
      <div className="lp-nav__inner">
        <a href="/#top" className="lp-nav__brand" aria-label="Overmatch home">
          <img src="/overmatch-logo-black.png" alt="Overmatch" />
          <span className="lp-nav__ai">AI</span>
        </a>

        <button
          type="button"
          ref={toggleRef}
          className="lp-nav__toggle"
          aria-expanded={open}
          aria-controls="lp-nav-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="lp-burger" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        </button>

        {/* One nav for both layouts — inline in the bar above the breakpoint,
            a drop panel below it — so the links aren't announced twice. */}
        <nav
          id="lp-nav-menu"
          className="lp-nav__links"
          data-open={open}
          aria-label="Primary"
        >
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="lp-nav__link"
              onClick={() => setOpen(false)}
            >
              {l.label}
            </a>
          ))}
          <a href="/#demo" className="lp-nav__cta" onClick={() => setOpen(false)}>
            Request a demo
          </a>
        </nav>
      </div>
    </header>
  )
}
